import { Injectable, computed, inject, signal } from '@angular/core';
import { MonMetadata } from '../generated/mon';
import { OnchainStateService } from './onchain-state.service';

@Injectable({ providedIn: 'root' })
export class GachaModalService {
  private readonly onchainState = inject(OnchainStateService);

  private readonly _showModal = signal<boolean>(false);
  readonly showModal = this._showModal.asReadonly();

  readonly totalMons = Object.keys(MonMetadata).length;

  readonly ownedCount = computed(() => {
    const owned = this.onchainState.ownedMonIndices();
    if (owned === undefined) return 0;
    return new Set(owned.map((id) => Number(id))).size;
  });

  readonly isSoldOut = computed(() => this.ownedCount() >= this.totalMons);

  // rollCost stays 0n until fetchRollCost lands, and 0 points clears a 0 cost —
  // so an unfetched cost must not read as affordable.
  readonly canAffordRoll = computed(
    () =>
      this.onchainState.rollCost() > 0n &&
      this.onchainState.pointsBalance() >= this.onchainState.rollCost(),
  );

  /** Idempotent: the underlying read early-returns once the cost is known. */
  ensureRollCost(): Promise<bigint> {
    return this.onchainState.fetchRollCost();
  }

  open() {
    this._showModal.set(true);
  }

  // Post-battle auto-open. Resolves the cost first so a never-fetched 0n cost
  // can't read as unaffordable; stays closed if the read fails.
  async openIfAffordable(): Promise<void> {
    try {
      await this.ensureRollCost();
    } catch (err) {
      console.error('failed to read gacha roll cost', err);
      return;
    }
    if (!this.canAffordRoll() || this.isSoldOut()) return;
    this.open();
  }

  close() {
    this._showModal.set(false);
  }
}
